import { FONT_FILES, fontFaceCss } from './base.js'
import { renderReceipt } from './receipt.js'
import { renderMedicalCert } from './medicalCert.js'
import { renderReferral } from './referral.js'
import { renderToothtaxiCheckup } from './toothtaxiCheckup.js'
import { renderConsent } from './consent.js'

/**
 * The documents that render from HTML templates instead of the Apps Script /
 * Google Docs renderer. Keyed by the SAME template names DocumentGenerator
 * already sends (receipt, receipt_en, …), so moving a document over is one line
 * here — the dialog and its payload builder do not change.
 *
 * Imported by both the browser (instant preview) and the Pages Function
 * (functions/api/documents/render.js → PDF). One template, so what staff accept
 * in the preview is what gets filed.
 */
export const HTML_TEMPLATES = {
  receipt:    (data, assets) => renderReceipt(data, { lang: 'th', assets }),
  receipt_en: (data, assets) => renderReceipt(data, { lang: 'en', assets }),
  invoice:       (data, assets) => renderReceipt(data, { lang: 'th', assets, kind: 'invoice' }),
  invoice_en:    (data, assets) => renderReceipt(data, { lang: 'en', assets, kind: 'invoice' }),
  fee_detail:    (data, assets) => renderReceipt(data, { lang: 'th', assets, kind: 'fee_detail' }),
  fee_detail_en: (data, assets) => renderReceipt(data, { lang: 'en', assets, kind: 'fee_detail' }),
  referral:      (data, assets) => renderReferral(data, { lang: 'th', assets }),
  referral_en:   (data, assets) => renderReferral(data, { lang: 'en', assets }),
  xray_referral: (data, assets) => renderReferral(data, { lang: 'th', assets, kind: 'xray' }),
  toothtaxi_checkup: (data, assets) => renderToothtaxiCheckup(data, { assets }),
  medical_cert:    (data, assets) => renderMedicalCert(data, { lang: 'th', assets }),
  medical_cert_en: (data, assets) => renderMedicalCert(data, { lang: 'en', assets }),
  // Signed consent — rendered by functions/api/consents/[token].js at accept time.
  consent:    (data, assets) => renderConsent(data, { lang: 'th', assets }),
  consent_en: (data, assets) => renderConsent(data, { lang: 'en', assets }),
}

export function isHtmlTemplate(template) {
  return Object.prototype.hasOwnProperty.call(HTML_TEMPLATES, template)
}

export function renderDocumentHtml(template, data, assets) {
  if (!isHtmlTemplate(template)) throw new Error(`unknown html template: ${template}`)
  return HTML_TEMPLATES[template](data || {}, assets)
}

// Static files every template uses, under public/doc/.
export const LOGO_FILE = 'wordmark.png'
export const TOOTHTAXI_LOGO_FILE = 'toothtaxi.png'   // the Tooth Taxi checkup form's letterhead
export const ASSET_FILES = [LOGO_FILE, TOOTHTAXI_LOGO_FILE, ...FONT_FILES.map((f) => `fonts/${f.file}`)]

/**
 * Build the `assets` a template needs from a resolver `src(path)` where path is
 * relative to /doc/ — e.g. 'fonts/IBMPlexSansThai-SemiBold.ttf'. The browser
 * passes absolute same-origin URLs; the server passes data: URIs.
 */
export function documentAssets(src) {
  return {
    logo: src(LOGO_FILE),
    toothtaxiLogo: src(TOOTHTAXI_LOGO_FILE),
    fontCss: fontFaceCss((file) => src(`fonts/${file}`)),
  }
}
